import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import autouploadMobileImage from 'img/promo-block-mobile/autoupload-mobile-modal.png';
import autouploadHighlightMobileImage from 'img/promo-block-mobile/autoupload-mobile-modal-highlight.png';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { helpAutoUploadHref } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { multiTariffHighlightWebview } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendSplashScreenAnalytics } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.analytics';

import styles from './UnlimitedUploadInfo.css';

export interface UnlimitedUploadInfoProps {
    onClose?: () => void;
}

export const UnlimitedUploadInfo = memo<UnlimitedUploadInfoProps>(({ onClose }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const sendAnalytics = (action) => sendSplashScreenAnalytics({ action, name: 'unlimited_autoupload_description' });

    useEffect(() => {
        sendAnalytics('show');
    }, []);

    const handleClose = useCallback(() => {
        sendAnalytics('close');
        onClose?.();
    }, [onClose]);

    const handleClick = useCallback(() => {
        onClose?.();
        sendAnalytics('click');
    }, [onClose]);

    const handleClickLink = useCallback(() => {
        sendAnalytics('click-details');
    }, []);

    return (
        <MobileDialog
            id="mobile-subscription-features-modal"
            onClose={handleClose}
            closeOnDimmerClick
            topmost
            scrollRef={scrollRef}
            mod="base"
            contextPadding="zero"
            innerClassNames={{
                close: styles.close,
            }}
        >
            <div className={styles.root} ref={scrollRef}>
                <div className={styles.imgBlock}>
                    <img
                        className={classNames(styles.img, {
                            [styles.img_highlight]: multiTariffHighlightWebview,
                        })}
                        src={multiTariffHighlightWebview ? autouploadHighlightMobileImage : autouploadMobileImage}
                        alt="Cover image"
                    />
                </div>
                <Spacing size={24} />
                <Text className={styles.title}>Как работает безлимит</Text>
                <Spacing size={12} />
                <Text className={styles.text}>
                    {multiTariffHighlightWebview ? (
                        <>
                            Или&nbsp;безлимитная автозагрузка&nbsp;— это&nbsp;когда фото и&nbsp;видео с&nbsp;телефона переносятся
                            в&nbsp;Облако, но&nbsp;не&nbsp;занимают в&nbsp;нём&nbsp;гигабайты
                        </>
                    ) : (
                        <>
                            Или безлимитная автозагрузка —&nbsp;это расширенная возможность обычной автозагрузки. С обычной фото и видео с
                            телефона переносятся в Облако и занимают в нём гигабайты.
                            <br />А с безлимитной —&nbsp;нет.
                        </>
                    )}
                </Text>
                <Spacing size={12} />
                <Text className={styles.text}>Доступен в тарифах от 256 ГБ</Text>
                <Spacing size={32} />
                <Button theme="vk" primary sizeMode={ButtonSizeMode.extraBig} className={styles.button} onClick={handleClick}>
                    {multiTariffHighlightWebview ? 'Понятно' : 'Супер, понятно'}
                </Button>
                <Spacing size={16} />
                {!!helpAutoUploadHref && (
                    <Text className={styles.linkBlock}>
                        Подробнее{' '}
                        <a
                            href={helpAutoUploadHref}
                            onClick={handleClickLink}
                            className={styles.link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            рассказали в статье
                        </a>{' '}
                        «Как включить безлимитную автозагрузку файлов с телефона в&nbsp;Облако Mail»
                    </Text>
                )}
            </div>
        </MobileDialog>
    );
});

UnlimitedUploadInfo.displayName = 'UnlimitedUploadInfo';
